import { CallAPIGetPromise, CallAPIPostPromise } from "Common/commaon";
import { useEffect, useState } from "react";
import { Toaster } from "components/ui/sonner";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Download, Pencil, Trash2 } from "lucide-react";
import "./sub-category-master.css";

const SubCategoryMasterModern = () => {
  const [category, setCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [price, setPrice] = useState("");
  const [id, setId] = useState("");
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [success, setSuccess] = useState("");
  const [list, setList] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [refresh, setRefresh] = useState(false);

  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  /* Fetch Categories for dropdown */
  useEffect(() => {
    CallAPIGetPromise("/service/getServices").then(
      (res: any) => {
        setCategories(res?.result || []);
      }
    );
  }, [refresh]);

  /* Fetch Sub Categories */
  useEffect(() => {
    const selectedCat = categories.find((c: any) => c.cat_name === category);
    if (!selectedCat) {
      setList([]);
      return;
    }
    CallAPIGetPromise(
      `/category/get-sub-categories/${selectedCat?.cat_id}`,
    ).then((res: any) => {
      setList(res?.result || []);
    });
  }, [category, refresh, categories]);

  const handleSaveSubCategory = () => {
    setError("");
    setWarning("");
    setSuccess("");
  
    if (saving) return;
  
    if (!category) {
      setWarning("Please select a category");
      return;
    }
  
    if (!subCategory.trim()) {
      setWarning("Sub category name is required");
      return;
    }
  
    if (!price) {
      setWarning("Price is required");
      return;
    }
  
    if (isNaN(Number(price))) {
      setError("Price must be a valid number");
      return;
    }
  
    const selectedCat = categories.find((c: any) => c.cat_name === category);
  
    if (!selectedCat) {
      setError("Invalid category selected");
      return;
    }
  
    // duplicate check
    const alreadyExists = list.some(
      (item) =>
        item.sc_category.toLowerCase() === subCategory.trim().toLowerCase()
    );
  
    if (alreadyExists && !id) {
      setWarning("Sub category already exists");
      return;
    }
  
    setSaving(true);
  
    CallAPIPostPromise("/category/create-sub-categories", {
      sc_main_cat_id: selectedCat.cat_id,
      sc_category: subCategory.trim(),
      sc_price: Number(price),
      sc_created_by: "admin",
    })
      .then(() => {
        setSuccess(id ? "Sub Category updated successfully" : "Sub Category created successfully");
        setSubCategory("");
        setPrice("");
        setId("");
        setRefresh((r) => !r);
      })
      .catch((err: any) => {
        setError(err?.message || "Something went wrong");
      })
      .finally(() => setSaving(false));
  };
  const handleExportAll = async () => {
    setError("");
    setWarning("");
  
    try {
      const res: any = await CallAPIGetPromise("/category/get-all-sub-categories");
  
      const data = res?.result || [];
  
      if (data.length === 0) {
        setWarning("No data to export");
        return;
      }
  
      const formatted = data.map((item: any, index: number) => ({
        "S.No": index + 1,
        "Category": item.cat_name,
        "Sub Category": item.sc_category,
        "Price":`$${item.sc_price}` 
      }));
  
      const worksheet = XLSX.utils.json_to_sheet(formatted);
      const workbook = XLSX.utils.book_new();
  
      XLSX.utils.book_append_sheet(workbook, worksheet, "All SubCategories");
  
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array"
      });
  
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
  
      saveAs(blob, "all_subcategories.xlsx");
  
      setSuccess("All subcategories exported 😌");
  
    } catch (err: any) {
      setError(err?.message || "Export failed");
    }
  };
  useEffect(() => {
    if (error || warning || success) {
      const timer = setTimeout(() => {
        setError("");
        setWarning("");
        setSuccess("");
      }, 3000);
  
      return () => clearTimeout(timer);
    }
  }, [error, warning, success]);
  const handleDelete = (deleteId: string) => {
    if (deletingId) return;

    setDeletingId(deleteId);

    CallAPIPostPromise(
      "/category/del-sub-categorie",
      { scId: deleteId }
    )
      .then(() => setRefresh((r) => !r))
      .finally(() => setDeletingId(null));
  };

  const resetForm = () => {
    setId("");
    setSubCategory("");
    setPrice("");
    setError("");
    setWarning("");
  };

  return (
    <div className="tailwind-prefix page-content sub-category-master-page">
      <div className="sub-category-master-shell space-y-6">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleExportAll}
            className="sub-category-export inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
          >
            <Download size={16} aria-hidden="true" />
            Export Excel
          </button>
        </div>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm" role="alert">
            {error}
          </div>
        )}

        {warning && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg text-sm" role="alert">
            {warning}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg text-sm" role="status">
            {success}
          </div>
        )}
        {/* Add / Edit Card */}
        <div className="sub-category-editor bg-white rounded-2xl shadow-sm border p-6">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 pb-4">
            <div>
              <h2 className="mb-0 text-lg font-semibold text-gray-900">{id ? "Edit subcategory" : "Add subcategory"}</h2>
              <p className="mb-0 mt-1 text-sm text-gray-500">Create and manage pricing for services within a category.</p>
            </div>
            {id && (
              <button type="button" onClick={resetForm} className="sub-category-secondary rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
                Cancel editing
              </button>
            )}
          </div>

          <div className="sub-category-form-grid">
            <div>
              <label htmlFor="subcategory-category" className="mb-2 block text-sm font-medium text-gray-700">Category</label>
              <select
                id="subcategory-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full border rounded-lg px-4 py-2.5"
              >
                <option value="">Select category</option>
                {categories.map((cat) => (
                  <option key={cat.cat_id} value={cat.cat_name}>{cat.cat_name}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="subcategory-name" className="mb-2 block text-sm font-medium text-gray-700">Subcategory name</label>
              <input
                id="subcategory-name"
                value={subCategory}
                onChange={(e) => setSubCategory(e.target.value)}
                placeholder="Enter subcategory name"
                className="w-full border rounded-lg px-4 py-2.5 focus:outline-none"
              />
            </div>

            <div>
              <label htmlFor="subcategory-price" className="mb-2 block text-sm font-medium text-gray-700">Price</label>
              <div className="sub-category-price-field">
                <span aria-hidden="true">$</span>
                <input
                  id="subcategory-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="0.00"
                  className="w-full border rounded-lg py-2.5 pr-4 focus:outline-none"
                />
              </div>
            </div>
          </div>

          <div className="sub-category-form-actions">
            {(id || subCategory || price) && (
              <button type="button" onClick={resetForm} className="sub-category-secondary rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50">
                Reset
              </button>
            )}
            <button
              type="button"
              onClick={handleSaveSubCategory}
              disabled={saving}
              className="sub-category-save inline-flex items-center justify-center gap-2 px-6 text-sm font-semibold"
            >
              {saving && (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              {saving ? "Saving..." : id ? "Update subcategory" : "Save subcategory"}
            </button>
          </div>
        </div>

        {/* List */}
        <div className="sub-category-list bg-white rounded-2xl shadow-sm border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-gray-500">
                <th className="p-4">Category</th>
                <th className="p-4">Sub Category</th>
                <th className="p-4">Price</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((item) => (
                <tr
                  key={item.sc_id}
                  className="border-t hover:bg-gray-50 transition"
                >
                  <td className="p-4 font-medium text-gray-700">
                    {category}
                  </td>

                  <td className="p-4 font-medium text-gray-900">
                    {item.sc_category}
                  </td>
                  <td className="p-4 font-medium text-gray-900">
                    ${item.sc_price}
                  </td>
                  <td className="p-4 text-right space-x-4">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setSubCategory(item.sc_category);
                          setPrice(item.sc_price);
                          setId(item.sc_id);
                        }}
                        className="sub-category-row-action inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        disabled={saving || deletingId === item.sc_id}
                      >
                        <Pencil size={14} aria-hidden="true" />
                        Edit
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDelete(item.sc_id)}
                        disabled={deletingId === item.sc_id}
                        className={`sub-category-row-action sub-category-row-action--delete inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 ${
                          deletingId === item.sc_id
                            ? "text-gray-400 cursor-not-allowed"
                            : "border-red-200 bg-transparent text-red-600 hover:bg-red-50"
                        }`}
                      >
                        {deletingId === item.sc_id && (
                          <span className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                        )}
                        {deletingId === item.sc_id ? <span className="sr-only">Deleting</span> : <Trash2 size={14} aria-hidden="true" />}
                        {deletingId === item.sc_id ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {list.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-10 text-center text-gray-400">
                    {category ? "No subcategories found for this category." : "Select a category to view its subcategories."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </div>
      <Toaster position="top-right" richColors />
    </div>

  );
};

export default SubCategoryMasterModern;
